import React from 'react';

import { Modal, Button, Table, TableSelection, TablePagination, Flashbar, Input } from 'AWS-UI-Components-React';

import { areFieldValuesEquivalent } from '../../../utils/product_line';
import { ERROR_MESSAGE } from '../../../constants/views';

const NO_CHANGE_MESSAGE = '<No changes>';
const tablePageSize = 10;

export class ReviewChanges extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProductLineIds: undefined,
    };
  }

  getColumnDefinitions = () => {
    return [
      {
        id: 'id',
        header: 'Id',
        cell: (item) => item.id,
      },
      {
        id: 'name',
        header: 'Name',
        cell: (item) => item.name,
      },
      {
        id: 'productGroups',
        header: 'Product Groups',
        cell: (item) => this.getDiffItemDisplay(item, 'productGroups'),
      },
      {
        id: 'parent',
        header: 'Parent',
        cell: (item) => this.getDiffItemDisplay(item, 'parent'),
      },
      {
        id: 'excluded',
        header: 'Excluded',
        cell: (item) => this.getDiffItemDisplay(item, 'excluded'),
      },
      {
        id: 'ignored',
        header: 'Ignored',
        cell: (item) => this.getDiffItemDisplay(item, 'ignored'),
      },
    ];
  };

  getDiffItemDisplay = (item, field) => {
    if (item[field].noChange) {
      return <i>{NO_CHANGE_MESSAGE}</i>;
    }
    return (
      <div>
        <div>{Array.isArray(item[field].updated) ? item[field].updated.join(', ') : item[field].updated}</div>
        <div>
          <del>{Array.isArray(item[field].removed) ? item[field].removed.join(', ') : item[field].removed}</del>
        </div>
      </div>
    );
  };

  getDiffItems = (stagedProductLines, latestProductLinesById, productGroupsById) => {
    if (!stagedProductLines) {
      return [];
    }

    return stagedProductLines.map((stagedProductLine) => {
      return {
        id: stagedProductLine.id,
        name: stagedProductLine.name,
        productGroups: this.getProductGroupDiff(
          latestProductLinesById[stagedProductLine.id].gls,
          stagedProductLine.gls,
          productGroupsById
        ),
        parent: this.getParentDiff(
          latestProductLinesById[stagedProductLine.id].parentItem,
          stagedProductLine.parentItem
        ),
        excluded: this.getExcludedDiff(
          latestProductLinesById[stagedProductLine.id].excluded,
          stagedProductLine.excluded
        ),
        ignored: this.getIgnoredDiff(
          latestProductLinesById[stagedProductLine.id].ignored,
          stagedProductLine.ignored
        ),
      };
    });
  };

  getProductGroupDiff = (originalGls, modifiedGls, productGroupsById) => {
    if (areFieldValuesEquivalent(originalGls, modifiedGls)) {
      return { noChange: true };
    }

    const updated = (modifiedGls || []).map((gl) => this.glDisplayFormat(gl, productGroupsById));
    const removed = (originalGls || [])
      .filter((gl) => !modifiedGls.includes(gl))
      .map((gl) => this.glDisplayFormat(gl, productGroupsById));

    return { updated, removed };
  };

  glDisplayFormat = (gl, productGroupsById) => {
    const productGroup = productGroupsById[gl.toString()];
    return productGroup ? `${productGroup.name} (${productGroup.id})` : 'None';
  };

  getParentDiff = (originalParent, modifiedParent) => {
    if (areFieldValuesEquivalent(originalParent, modifiedParent)) {
      return { noChange: true };
    }

    const updated = modifiedParent ? `${modifiedParent.name} (${modifiedParent.id})` : 'None';
    const removed = originalParent ? `${originalParent.name} (${originalParent.id})` : 'None';
    return { updated, removed };
  };

  getExcludedDiff = (originalExcluded, modifiedExcluded) => {
    if (areFieldValuesEquivalent(originalExcluded, modifiedExcluded)) {
      return { noChange: true };
    }

    const updated = Boolean(modifiedExcluded) ? 'Yes' : 'No';
    const removed = Boolean(originalExcluded) ? 'Yes' : 'No';
    return { updated, removed };
  };

  getIgnoredDiff = (originalIgnored, modifiedIgnored) => {
    if (areFieldValuesEquivalent(originalIgnored, modifiedIgnored)) {
      return { noChange: true };
    }

    const updated = Boolean(modifiedIgnored) ? 'Yes' : 'No';
    const removed = Boolean(originalIgnored) ? 'Yes' : 'No';
    return { updated, removed };
  };

  onSelectionChange = (event) => {
    const selectedProductLineIds = event.detail.selectedItems.map((item) => item.id);
    this.setState({
      selectedProductLineIds,
    });
  };

  getSelectedCount = (selectedItems) => {
    return selectedItems && selectedItems.length ? <span>({selectedItems.length})</span> : null;
  };

  unstageSelected = (selectedItems) => {
    const selectedProductLineIds = selectedItems.map((item) => item.id);
    const { latestProductLinesById } = this.props;
    if (selectedProductLineIds) {
      this.props.unstageProductLineChanges(selectedProductLineIds, latestProductLinesById);
    }

    this.setState({
      selectedProductLineIds: [],
    });
  };

  publishSelected = (selectedItems) => {
    const selectedProductLineIds = selectedItems.map((item) => item.id);
    const { stagedProductLines, latestProductLinesById } = this.props;
    const stagedProductLinesWithComment = stagedProductLines.map((p) => {
      return { ...p, comment: this.state.comment ? this.state.comment : this.props.comment };
    })
    if (selectedProductLineIds) {
      this.props.publishProductLineChanges(selectedProductLineIds, latestProductLinesById, stagedProductLinesWithComment);
    }

    this.setState({
      selectedProductLineIds: [],
    });
  };

  onDismiss = () => {
    this.props.onDismiss();

    this.setState({
      selectedProductLineIds: undefined,
    });
  };

  onUpdateComment = (event) => {
    this.setState({ comment: event.detail.value });
  };

  render() {
    const {
      visible,
      comment,
      stagedProductLines,
      latestProductLinesById,
      productGroupsById,
      expandToFit = true,
      size = 'large',
      errorScreenMessage,
    } = this.props;

    const { selectedProductLineIds } = this.state;

    const diffItems = this.getDiffItems(stagedProductLines, latestProductLinesById, productGroupsById);

    const selectedItems =
      selectedProductLineIds === undefined
        ? diffItems
        : diffItems.filter((item) => selectedProductLineIds.includes(item.id));

    const content = (
      <div>
        <Table
          columnDefinitions={this.getColumnDefinitions()}
          items={diffItems}
          empty={'No staged changes.'}
          stickyHeader
        >
          <TableSelection onSelectionChange={this.onSelectionChange} trackBy="id" selectedItems={selectedItems} />
          <TablePagination pageSize={tablePageSize} />
        </Table>
        <h3>Comments:</h3>
        <div>
          <Input value={comment} onChange={this.onUpdateComment}/>
          <p></p>
        </div>
      </div>
    );

    return (
      <div>
        <Modal
          visible={visible}
          header={'Review/Publish Changes'}
          onDismiss={this.onDismiss}
          expandToFit={expandToFit}
          size={size}
          footer={
            <span className="awsui-util-f-r">
              <Button
                key={'UNSTAGE_SELECTED_BUTTON'}
                variant={'normal'}
                disabled={!this.getSelectedCount(selectedItems)}
                onClick={() => this.unstageSelected(selectedItems)}
              >
                Revert Selected {this.getSelectedCount(selectedItems)}
              </Button>
              <Button
                key={'PUBLISH_SELECTED_BUTTON'}
                variant={'primary'}
                disabled={!this.getSelectedCount(selectedItems)}
                onClick={() => this.publishSelected(selectedItems)}
              >
                Publish Selected {this.getSelectedCount(selectedItems)}
              </Button>
            </span>
          }
        >
          <div>
            <div>
              <Flashbar
                items={
                  errorScreenMessage
                    ? [
                        {
                          type: 'error',
                          content: `${ERROR_MESSAGE}${errorScreenMessage}`,
                        },
                      ]
                    : []
                }
              />
            </div>
            {content}
            <div style={{ textAlign: 'center', fontStyle: 'italic' }}>
              <p>Impact of published changes on data will be visible after the next data refresh.</p>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/pl_gl_mapping/review_changes.jsx